import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaPaperclip, FaSave, FaTimes } from "react-icons/fa";
import { saveInquiryBatchReply } from "./inquiryBatchApi";

const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".eml", ".msg"];

function isAcceptedFile(file) {
  if (!file?.name) return false;
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function InquiryBatchReplyDock({
  open,
  batchId,
  items = [],
  onClose,
  onSaved,
}) {
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [targetItems, setTargetItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const notesRef = useRef(null);
  const fileInputRef = useRef(null);
  const targetIdsRef = useRef([]);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setTargetItems([]);
      targetIdsRef.current = [];
      return undefined;
    }

    if (targetIdsRef.current.length > 0) return undefined;

    const snapshot = (Array.isArray(items) ? items : []).filter(
      (item) => item?.id != null,
    );
    if (snapshot.length === 0) return undefined;

    setTargetItems(snapshot);
    targetIdsRef.current = snapshot.map((item) => Number(item.id));
    setNotes("");
    setFiles([]);
    setSubmitting(false);
    setError("");
    setIsDragging(false);
    dragCounterRef.current = 0;

    const timer = window.setTimeout(() => notesRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open, batchId, items]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, submitting]);

  if (!open || targetItems.length === 0) return null;

  const addFiles = (incoming) => {
    const next = Array.from(incoming || []).filter(isAcceptedFile);
    if (next.length) {
      setFiles((prev) => [...prev, ...next]);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    if (event.dataTransfer?.types?.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (submitting) return;
    addFiles(event.dataTransfer?.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const trimmed = String(notes || "").trim();
    if (!trimmed && files.length === 0) {
      setError("Add notes and/or attach a file.");
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const batchItemIds =
        targetIdsRef.current.length > 0
          ? targetIdsRef.current
          : targetItems.map((item) => Number(item.id));

      const result = await saveInquiryBatchReply(batchId, {
        batch_item_ids: batchItemIds,
        notes: trimmed,
        documents: files,
      });
      onSaved?.(result);
    } catch (err) {
      setError(err?.message || "Failed to save reply.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-2 sm:p-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inquiry-reply-modal-title"
        onClick={handleClose}
      >
        {/* Entire modal is the drop target */}
        <div
          className={`tailwind-container relative z-10 flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border bg-white shadow-xl transition ${
            isDragging
              ? "border-violet-400 ring-2 ring-violet-300 ring-offset-1"
              : "border-slate-200"
          }`}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag overlay — covers the whole modal when dragging */}
          {isDragging ? (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-violet-50/90">
              <div className="rounded-lg border-2 border-dashed border-violet-400 bg-white px-6 py-4 text-center shadow-sm">
                <FaPaperclip className="mx-auto mb-1 text-violet-500" size={22} />
                <p className="mb-0 text-[13px] font-semibold text-violet-700">
                  Drop files to attach
                </p>
                <p className="mb-0 text-[11px] text-violet-500">
                  jpg, png, pdf, doc, docx, eml, msg
                </p>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
              <div className="min-w-0">
                <h3
                  id="inquiry-reply-modal-title"
                  className="mb-0 text-[13px] font-semibold text-slate-900"
                >
                  Record reply
                </h3>
                <p className="mb-0 text-[12px] text-slate-500">
                  Notes + optional files for {targetItems.length} student
                  {targetItems.length === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                style={{ boxShadow: "none" }}
                aria-label="Close"
              >
                <FaTimes size={12} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-2.5">
              <div className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
                {targetItems.map((item) => (
                  <span
                    key={item.id}
                    className="inline-flex max-w-full items-center rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200"
                  >
                    <span className="truncate">
                      {item.student_name || "Student"}
                      {item.provider_name ? ` · ${item.provider_name}` : ""}
                    </span>
                  </span>
                ))}
              </div>

              <textarea
                ref={notesRef}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                disabled={submitting}
                placeholder="Paste provider reply notes here…"
                className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
              />

              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.eml,.msg"
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  style={{ boxShadow: "none" }}
                >
                  <FaPaperclip size={11} />
                  Attach files
                </button>
                <span className="text-[11px] text-slate-500">
                  or drag & drop anywhere on this modal
                </span>
                {files.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex max-w-[180px] items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                  >
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-slate-400 hover:text-rose-600"
                      style={{
                        border: "none",
                        background: "transparent",
                        padding: 0,
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <FaTimes size={9} />
                    </button>
                  </span>
                ))}
              </div>

              {error ? (
                <p className="mb-0 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12px] font-medium text-rose-700">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 px-3 py-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                style={{ boxShadow: "none" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-violet-600 px-3 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:cursor-wait disabled:opacity-60"
                style={{ border: "none", boxShadow: "none" }}
              >
                <FaSave size={11} />
                {submitting ? "Saving…" : "Save reply"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default InquiryBatchReplyDock;